import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Save, DatabaseBackup, MapPin } from "lucide-react";
import EnvMigration from "@/pages/admin/EnvMigration";
import TrackingSettings from "@/pages/admin/TrackingSettings";
import SecurityPostureCard from "@/components/admin/SecurityPostureCard";

export default function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [form, setForm] = useState({});
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const save = useMutation({
    mutationFn: (p) => api.patch("/settings", p),
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["settings"] }); },
  });

  // 2FA
  const { data: tfaStatus } = useQuery({ queryKey: ["2fa-status"], queryFn: () => api.get("/2fa/status").then((r) => r.data) });
  const [tfaSetup, setTfaSetup] = useState(null);
  const [tfaCode, setTfaCode] = useState("");
  const start2fa = async () => {
    const { data } = await api.post("/2fa/setup");
    setTfaSetup(data);
  };
  const enable2fa = useMutation({
    mutationFn: () => api.post("/2fa/enable", { code: tfaCode }),
    onSuccess: () => { toast.success("2FA enabled ✓"); setTfaSetup(null); setTfaCode(""); qc.invalidateQueries({ queryKey: ["2fa-status"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Invalid code"),
  });
  const disable2fa = useMutation({
    mutationFn: () => api.post("/2fa/disable", { code: tfaCode }),
    onSuccess: () => { toast.success("2FA disabled"); setTfaCode(""); qc.invalidateQueries({ queryKey: ["2fa-status"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Invalid code"),
  });

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Company profile, GST config, banking, security.</p>
      </div>

      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST & Tax</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Bank</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice Customization</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security · 2FA</TabsTrigger>
          <TabsTrigger value="posture" data-testid="tab-posture">
            <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Posture
          </TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking">
            <MapPin className="mr-1.5 h-3.5 w-3.5" /> Live Tracking
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <DatabaseBackup className="mr-1.5 h-3.5 w-3.5" /> Backup &amp; Migration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card><CardContent className="grid grid-cols-2 gap-3 p-6">
            <div className="col-span-2"><Label>Company name</Label><Input value={form.company_name || ""} onChange={set("company_name")} data-testid="s-name" /></div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.company_address || ""} onChange={set("company_address")} rows={2} /></div>
            <div><Label>State</Label><Input value={form.state || ""} onChange={set("state")} /></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} /></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={set("email")} /></div>
            <div><Label>Website</Label><Input value={form.website || ""} onChange={set("website")} /></div>
            <div className="col-span-2"><Label>Logo URL (S3/CDN)</Label><Input value={form.logo_url || ""} onChange={set("logo_url")} placeholder="https://cdn.example.com/logo.png" data-testid="s-logo" /></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="gst" className="mt-4">
          <Card><CardContent className="grid grid-cols-2 gap-3 p-6">
            <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={set("gstin")} data-testid="s-gstin" /></div>
            <div><Label>PAN</Label><Input value={form.pan || ""} onChange={set("pan")} /></div>
            <div><Label>Default HSN code</Label><Input value={form.default_hsn_code || ""} onChange={set("default_hsn_code")} placeholder="27101930" /></div>
            <div><Label>Default GST rate (%)</Label><Input type="number" step="0.1" value={form.default_gst_rate || 0} onChange={set("default_gst_rate")} /></div>
            <div><Label>CGST rate (%)</Label><Input type="number" step="0.1" value={form.cgst_rate || 0} onChange={set("cgst_rate")} /></div>
            <div><Label>SGST rate (%)</Label><Input type="number" step="0.1" value={form.sgst_rate || 0} onChange={set("sgst_rate")} /></div>
            <div><Label>IGST rate (%)</Label><Input type="number" step="0.1" value={form.igst_rate || 0} onChange={set("igst_rate")} /></div>
            <div><Label>TDS rate (%)</Label><Input type="number" step="0.1" value={form.tds_rate || 0} onChange={set("tds_rate")} /></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="bank" className="mt-4">
          <Card><CardContent className="grid grid-cols-2 gap-3 p-6">
            <div><Label>Bank name</Label><Input value={form.bank_name || ""} onChange={set("bank_name")} /></div>
            <div><Label>Account no</Label><Input value={form.bank_account || ""} onChange={set("bank_account")} /></div>
            <div><Label>IFSC</Label><Input value={form.bank_ifsc || ""} onChange={set("bank_ifsc")} /></div>
            <div><Label>Branch</Label><Input value={form.bank_branch || ""} onChange={set("bank_branch")} /></div>
            <div><Label>Default price/L</Label><Input type="number" step="0.1" value={form.default_price_per_litre || 0} onChange={set("default_price_per_litre")} /></div>
            <div><Label>Default credit days</Label><Input type="number" value={form.default_credit_days || 0} onChange={set("default_credit_days")} /></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoice" className="mt-4">
          <Card><CardContent className="grid grid-cols-2 gap-3 p-6">
            <div><Label>Invoice prefix</Label><Input value={form.invoice_prefix || ""} onChange={set("invoice_prefix")} placeholder="INV" data-testid="s-inv-prefix" /></div>
            <div><Label>Challan prefix</Label><Input value={form.challan_prefix || ""} onChange={set("challan_prefix")} placeholder="DC" /></div>
            <div className="col-span-2 flex items-center gap-6 rounded-md border border-border/60 px-4 py-3">
              <div className="flex-1">
                <div className="text-sm font-semibold">Financial-year numbering</div>
                <div className="text-xs text-muted-foreground">Format: {form.invoice_prefix || "INV"}/2025-26/0001 (Indian FY starts April)</div>
              </div>
              <Switch checked={!!form.invoice_use_fy_numbering} onCheckedChange={(v) => setForm({ ...form, invoice_use_fy_numbering: v, challan_use_fy_numbering: v })} data-testid="s-fy-numbering" />
            </div>
            <div className="col-span-2">
              <Label>Default invoice PDF template (customer download)</Label>
              <div className="mt-1 grid grid-cols-2 gap-3">
                <label className={`flex cursor-pointer flex-col rounded-md border-2 p-3 text-sm transition ${(form.invoice_template_default || "standard") === "standard" ? "border-primary bg-primary/5" : "border-border/60"}`}>
                  <input type="radio" className="hidden" name="tmpl" value="standard" checked={(form.invoice_template_default || "standard") === "standard"} onChange={() => setForm({ ...form, invoice_template_default: "standard" })} data-testid="tmpl-standard" />
                  <div className="flex items-center justify-between">
                    <div className="font-bold">Standard</div>
                    <span className={`text-xs rounded-full px-2 py-0.5 ${(form.invoice_template_default || "standard") === "standard" ? "bg-primary text-white" : "bg-muted"}`}>PDF · A4 · Corporate</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Clean printable layout with branded header, HSN, GST split, bank details, terms, and signatory block. Ideal for email delivery.</div>
                </label>
                <label className={`flex cursor-pointer flex-col rounded-md border-2 p-3 text-sm transition ${form.invoice_template_default === "spreadsheet" ? "border-primary bg-primary/5" : "border-border/60"}`}>
                  <input type="radio" className="hidden" name="tmpl" value="spreadsheet" checked={form.invoice_template_default === "spreadsheet"} onChange={() => setForm({ ...form, invoice_template_default: "spreadsheet" })} data-testid="tmpl-xlsx" />
                  <div className="flex items-center justify-between">
                    <div className="font-bold">Spreadsheet</div>
                    <span className={`text-xs rounded-full px-2 py-0.5 ${form.invoice_template_default === "spreadsheet" ? "bg-primary text-white" : "bg-muted"}`}>PDF · A4 · Tabular grid</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Dense grid layout — every field bordered like a ledger sheet. Preferred by accountants and GST auditors.</div>
                </label>
              </div>
            </div>
            <div><Label>Signatory name</Label><Input value={form.signatory_name || ""} onChange={set("signatory_name")} placeholder="For FuelExpress Solutions Pvt Ltd" /></div>
            <div><Label>Signatory designation</Label><Input value={form.signatory_designation || ""} onChange={set("signatory_designation")} placeholder="Authorised Signatory" /></div>
            <div><Label>Theme color</Label><Input value={form.invoice_theme_color || "#f97316"} onChange={set("invoice_theme_color")} placeholder="#f97316" /></div>
            <div className="flex items-center gap-3 rounded-md border border-border/60 px-4 py-3">
              <Switch checked={form.invoice_show_bank_details !== false} onCheckedChange={(v) => setForm({ ...form, invoice_show_bank_details: v })} />
              <div className="text-sm">Show bank details on invoice PDF</div>
            </div>
            <div className="col-span-2"><Label>Terms & conditions</Label><Textarea value={form.terms || ""} onChange={set("terms")} rows={3} data-testid="s-terms" /></div>
            <div className="col-span-2"><Label>Invoice notes</Label><Textarea value={form.invoice_notes || ""} onChange={set("invoice_notes")} rows={2} placeholder="Thank you for your business!" /></div>
            <div className="col-span-2"><Label>Footer text</Label><Input value={form.invoice_footer || ""} onChange={set("invoice_footer")} placeholder="This is a computer-generated invoice." /></div>

            <div className="col-span-2 mt-4 rounded-lg border-2 border-dashed border-border/60 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Live Preview</div>
                <div className="inline-flex overflow-hidden rounded-full border border-border/60 text-xs">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, invoice_template_default: "standard" })}
                    className={`px-3 py-1 font-semibold uppercase tracking-widest transition ${(form.invoice_template_default || "standard") === "standard" ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}
                    data-testid="preview-toggle-standard"
                  >Standard</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, invoice_template_default: "spreadsheet" })}
                    className={`px-3 py-1 font-semibold uppercase tracking-widest transition ${form.invoice_template_default === "spreadsheet" ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}
                    data-testid="preview-toggle-spreadsheet"
                  >Spreadsheet</button>
                </div>
              </div>
              {(form.invoice_template_default === "spreadsheet")
                ? <SpreadsheetPreview form={form} />
                : <StandardPreview form={form} />}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <Card><CardContent className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck size={18} /> Two-Factor Authentication</div>
                <div className="text-sm text-muted-foreground">Protect your account with an authenticator app (Google Authenticator, Authy).</div>
              </div>
              <div className={`rounded-full px-3 py-1 text-xs font-bold ${tfaStatus?.enabled ? "bg-status-delivered/10 text-status-delivered" : "bg-muted text-muted-foreground"}`}>
                {tfaStatus?.enabled ? "ENABLED" : "DISABLED"}
              </div>
            </div>

            {!tfaStatus?.enabled && !tfaSetup && (
              <Button onClick={start2fa} data-testid="btn-setup-2fa">Setup 2FA</Button>
            )}
            {tfaSetup && (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
                <div className="mb-2 text-sm font-semibold">Scan this QR with Google Authenticator / Authy:</div>
                <img src={`data:image/png;base64,${tfaSetup.qr_png_base64}`} alt="2FA QR" className="mx-auto rounded border border-border" />
                <div className="mt-2 text-xs text-muted-foreground">Or enter this secret manually: <code className="font-mono">{tfaSetup.secret}</code></div>
                <div className="mt-4 space-y-2">
                  <Label>Enter the 6-digit code from your app</Label>
                  <Input value={tfaCode} onChange={(e) => setTfaCode(e.target.value)} maxLength={6} data-testid="2fa-code" />
                  <Button onClick={() => enable2fa.mutate()} disabled={tfaCode.length !== 6} data-testid="btn-enable-2fa">Enable 2FA</Button>
                </div>
              </div>
            )}
            {tfaStatus?.enabled && (
              <div className="rounded-lg border border-status-cancelled/40 bg-status-cancelled/5 p-4">
                <div className="mb-2 text-sm">To disable, confirm with your current 2FA code:</div>
                <div className="flex gap-2">
                  <Input value={tfaCode} onChange={(e) => setTfaCode(e.target.value)} maxLength={6} className="max-w-32" />
                  <Button variant="destructive" onClick={() => disable2fa.mutate()} disabled={tfaCode.length !== 6} data-testid="btn-disable-2fa">Disable 2FA</Button>
                </div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tracking" className="mt-4">
          <TrackingSettings settings={form} onChange={(patch) => setForm({ ...form, ...patch })} onSave={() => save.mutate(form)} saving={save.isPending} />
        </TabsContent>

        <TabsContent value="posture" className="mt-4">
          <SecurityPostureCard />
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          <EnvMigration embedded />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="btn-save-settings"><Save size={14} className="mr-1" /> {save.isPending ? "Saving…" : "Save settings"}</Button>
      </div>
    </div>
  );
}

/* ============================ INVOICE PREVIEWS ============================ */
const SAMPLE = {
  invoice_no: (prefix, fy) => fy ? `${prefix || "INV"}/2025-26/0001` : `${prefix || "INV"}-0001`,
  invoice_date: "2026-02-09",
  order_code: "ORD-0025",
  customer_name: "Sunrise Logistics Pvt Ltd",
  customer_address: "Plot 47, Chakan MIDC Phase-2\nPune, Maharashtra - 410501",
  customer_gstin: "27AABCS9876M1ZX",
  customer_pan: "AABCS9876M",
  hsn: "27101930",
  qty: 1500,
  rate: 95.00,
  subtotal: 142500,
  cgst_rate: 9,
  sgst_rate: 9,
  cgst: 12825,
  sgst: 12825,
  total: 168150,
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CompanyHeader({ form, theme, compact = false }) {
  return (
    <div className={compact ? "text-xs" : "text-sm"}>
      {form.logo_url && <img src={form.logo_url} alt="logo" className="mb-2 h-10 object-contain" onError={(e) => (e.target.style.display = "none")} />}
      <div className="font-display font-black" style={{ color: theme, fontSize: compact ? 16 : 20 }}>{form.company_name || "Your Company"}</div>
      <div className="text-muted-foreground whitespace-pre-line text-[11px]">{form.company_address || "—"}</div>
      <div className="mt-1 text-[11px]">
        <span className="font-semibold">GSTIN:</span> <span className="font-mono">{form.gstin || "—"}</span>
        {" · "}<span className="font-semibold">PAN:</span> <span className="font-mono">{form.pan || "—"}</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {form.phone || "—"} · {form.email || "—"}
      </div>
    </div>
  );
}

function StandardPreview({ form }) {
  const theme = form.invoice_theme_color || "#f97316";
  const invNo = SAMPLE.invoice_no(form.invoice_prefix, form.invoice_use_fy_numbering);
  return (
    <div className="mx-auto max-w-3xl rounded-md bg-white p-6 text-black shadow-md" style={{ fontFamily: "system-ui, sans-serif" }} data-testid="preview-standard">
      <div className="flex items-start justify-between border-b pb-3" style={{ borderColor: theme }}>
        <CompanyHeader form={form} theme={theme} />
        <div className="text-right">
          <div className="rounded px-3 py-1 text-xs font-black uppercase tracking-widest text-white" style={{ background: theme }}>Tax Invoice</div>
          <div className="mt-2 font-mono text-sm font-bold">{invNo}</div>
          <div className="mt-0.5 text-[11px] text-gray-500">Dated {SAMPLE.invoice_date}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Bill To</div>
          <div className="mt-1 text-sm font-bold">{SAMPLE.customer_name}</div>
          <div className="text-[11px] text-gray-600 whitespace-pre-line">{SAMPLE.customer_address}</div>
          <div className="mt-1 text-[11px]">
            GSTIN: <span className="font-mono">{SAMPLE.customer_gstin}</span> · PAN: <span className="font-mono">{SAMPLE.customer_pan}</span>
          </div>
        </div>
        <div className="text-right text-[11px]">
          <div><span className="text-gray-500">Order:</span> <span className="font-mono">{SAMPLE.order_code}</span></div>
          <div><span className="text-gray-500">HSN:</span> <span className="font-mono">{SAMPLE.hsn}</span></div>
          <div><span className="text-gray-500">Place of Supply:</span> <span>Maharashtra</span></div>
        </div>
      </div>

      <table className="mt-4 w-full text-[11px]" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "40%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr className="text-white" style={{ background: theme }}>
            <th className="p-2 text-center">Sr</th>
            <th className="p-2 text-left">Description</th>
            <th className="p-2 text-center">HSN</th>
            <th className="p-2 text-right">Qty (L)</th>
            <th className="p-2 text-right">Rate</th>
            <th className="p-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-200">
            <td className="p-2 text-center">1</td>
            <td className="p-2 text-left">High Speed Diesel (HSD)</td>
            <td className="p-2 text-center font-mono">{SAMPLE.hsn}</td>
            <td className="p-2 text-right font-mono">{SAMPLE.qty.toFixed(2)}</td>
            <td className="p-2 text-right font-mono">{SAMPLE.rate.toFixed(2)}</td>
            <td className="p-2 text-right font-mono">{SAMPLE.subtotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className="mt-3 flex justify-end">
        <table className="w-1/2 text-[11px]" style={{ tableLayout: "fixed" }}>
          <colgroup><col style={{ width: "60%" }} /><col style={{ width: "40%" }} /></colgroup>
          <tbody>
            <tr><td className="py-1 pr-2 text-right text-gray-600">Subtotal:</td><td className="py-1 pl-2 pr-2 text-right font-mono">{fmt(SAMPLE.subtotal)}</td></tr>
            <tr><td className="py-1 pr-2 text-right text-gray-600">CGST @ {SAMPLE.cgst_rate}%:</td><td className="py-1 pl-2 pr-2 text-right font-mono">{fmt(SAMPLE.cgst)}</td></tr>
            <tr><td className="py-1 pr-2 text-right text-gray-600">SGST @ {SAMPLE.sgst_rate}%:</td><td className="py-1 pl-2 pr-2 text-right font-mono">{fmt(SAMPLE.sgst)}</td></tr>
            <tr className="font-black" style={{ background: "#FEF3C7" }}>
              <td className="py-1.5 pr-2 text-right">GRAND TOTAL (INR):</td>
              <td className="py-1.5 pl-2 pr-2 text-right font-mono">{fmt(SAMPLE.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 text-[11px]">
        {form.invoice_show_bank_details !== false && (
          <div>
            <div className="font-bold uppercase tracking-widest text-gray-500 text-[10px]">Bank Details</div>
            <div className="mt-1">{form.bank_name || "—"}</div>
            <div className="font-mono">A/c {form.bank_account || "—"} · IFSC {form.bank_ifsc || "—"}</div>
            <div className="text-gray-500">Branch: {form.bank_branch || "—"}</div>
          </div>
        )}
        <div className={form.invoice_show_bank_details === false ? "col-span-2 text-right" : "text-right"}>
          <div className="font-bold">For {form.company_name || "Your Company"}</div>
          <div className="mt-6 border-t border-gray-300 pt-1">{form.signatory_name || "____________________"}</div>
          <div className="text-gray-500 italic">{form.signatory_designation || "Authorised Signatory"}</div>
        </div>
      </div>

      <div className="mt-3 text-[10px]">
        <div className="font-bold uppercase tracking-widest text-gray-500">Terms & Conditions</div>
        <div className="mt-1 whitespace-pre-wrap text-gray-600">{form.terms || "—"}</div>
      </div>

      {form.invoice_notes && <div className="mt-3 rounded bg-gray-50 p-2 text-[11px] italic text-gray-700">{form.invoice_notes}</div>}
      {form.invoice_footer && <div className="mt-2 text-center text-[9px] uppercase tracking-widest text-gray-400">{form.invoice_footer}</div>}
    </div>
  );
}

// Unified 7-column grid (widths sum = 100%) reused across every band of the sheet.
const SPREADSHEET_COL_WIDTHS = [6, 34, 12, 12, 12, 12, 12];
const SpreadsheetCols = () => (
  <colgroup>
    {SPREADSHEET_COL_WIDTHS.map((w, idx) => (
      <col key={`col-${idx}-${w}`} style={{ width: `${w}%` }} />
    ))}
  </colgroup>
);

function SpreadsheetPreview({ form }) {
  const theme = form.invoice_theme_color || "#f97316";
  const invNo = SAMPLE.invoice_no(form.invoice_prefix, form.invoice_use_fy_numbering);
  const cellBase = "border border-gray-400 px-2 py-1 text-[10px] align-top";
  const hdrCell = `${cellBase} bg-gray-100 font-bold uppercase tracking-widest text-gray-700 text-[9px]`;

  const Cols = SpreadsheetCols;

  return (
    <div className="mx-auto max-w-3xl bg-white p-3 text-black shadow-md" style={{ fontFamily: "system-ui, sans-serif" }} data-testid="preview-spreadsheet">
      {/* Header band */}
      <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
        <Cols />
        <tbody>
          <tr>
            <td colSpan={5} className={`${cellBase} text-white font-bold text-sm`} style={{ background: theme }}>
              {form.logo_url && <img src={form.logo_url} alt="" className="inline mr-2 h-6 align-middle" onError={(e) => (e.target.style.display = "none")} />}
              {form.company_name || "Your Company"}
            </td>
            <td colSpan={2} className={`${cellBase} text-white font-bold text-right`} style={{ background: theme }}>Tax Invoice</td>
          </tr>
          <tr>
            <td colSpan={5} className={cellBase + " text-gray-600 whitespace-pre-line"}>{form.company_address || "—"}</td>
            <td colSpan={2} className={cellBase + " text-right text-gray-500 italic"}>Original for Recipient</td>
          </tr>
          <tr>
            <td colSpan={3} className={cellBase}><span className="text-gray-500">GSTIN:</span> <span className="font-mono">{form.gstin || "—"}</span></td>
            <td colSpan={2} className={cellBase}><span className="text-gray-500">PAN:</span> <span className="font-mono">{form.pan || "—"}</span></td>
            <td colSpan={2} className={cellBase}><span className="text-gray-500">Phone:</span> {form.phone || "—"}</td>
          </tr>
        </tbody>
      </table>

      {/* Meta band */}
      <table className="w-full border-collapse -mt-px" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "15%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "17%" }} />
        </colgroup>
        <tbody>
          <tr>
            <td className={hdrCell}>Invoice No.</td>
            <td className={cellBase + " font-bold"}>{invNo}</td>
            <td className={hdrCell}>Date</td>
            <td className={cellBase + " font-bold"}>{SAMPLE.invoice_date}</td>
            <td className={hdrCell}>Order No.</td>
            <td className={cellBase + " font-bold"}>{SAMPLE.order_code}</td>
          </tr>
          <tr>
            <td className={hdrCell}>HSN</td>
            <td className={cellBase + " font-mono"}>{SAMPLE.hsn}</td>
            <td className={hdrCell}>Place of Supply</td>
            <td className={cellBase}>Maharashtra</td>
            <td className={hdrCell}>Payment</td>
            <td className={cellBase + " font-bold uppercase"}>UNPAID</td>
          </tr>
        </tbody>
      </table>

      {/* Bill To / Ship To band */}
      <table className="w-full border-collapse -mt-px" style={{ tableLayout: "fixed" }}>
        <colgroup><col style={{ width: "50%" }} /><col style={{ width: "50%" }} /></colgroup>
        <tbody>
          <tr>
            <td className={hdrCell}>Bill To (Buyer)</td>
            <td className={hdrCell}>Ship To</td>
          </tr>
          <tr>
            <td className={cellBase + " font-bold"}>{SAMPLE.customer_name}</td>
            <td className={cellBase + " font-bold"}>{SAMPLE.customer_name}</td>
          </tr>
          <tr>
            <td className={cellBase + " whitespace-pre-line"} style={{ height: 60 }}>
              {SAMPLE.customer_address}
              {"\n"}GSTIN: <span className="font-mono">{SAMPLE.customer_gstin}</span>
            </td>
            <td className={cellBase + " whitespace-pre-line"}>
              {SAMPLE.customer_address}
              {"\n"}GSTIN: <span className="font-mono">{SAMPLE.customer_gstin}</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Line items — 8 columns w/ explicit widths */}
      <table className="w-full border-collapse -mt-px" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <thead>
          <tr className="text-white" style={{ background: theme }}>
            <th className={cellBase + " text-white text-center"}>Sr</th>
            <th className={cellBase + " text-white text-left"}>Description of Goods</th>
            <th className={cellBase + " text-white text-center"}>HSN</th>
            <th className={cellBase + " text-white text-center"}>UOM</th>
            <th className={cellBase + " text-white text-right"}>Qty</th>
            <th className={cellBase + " text-white text-right"}>Rate</th>
            <th className={cellBase + " text-white text-right"}>Taxable</th>
            <th className={cellBase + " text-white text-right"}>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={cellBase + " text-center"}>1</td>
            <td className={cellBase + " text-left"}>High Speed Diesel (HSD)</td>
            <td className={cellBase + " text-center font-mono"}>{SAMPLE.hsn}</td>
            <td className={cellBase + " text-center"}>L</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.qty.toFixed(2)}</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.rate.toFixed(2)}</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.subtotal.toFixed(2)}</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.subtotal.toFixed(2)}</td>
          </tr>
          {/* Empty ledger-style rows */}
          {[0, 1, 2].map((i) => (
            <tr key={i}>
              <td className={cellBase}>&nbsp;</td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
              <td className={cellBase}></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals band — same 8 cols */}
      <table className="w-full border-collapse -mt-px" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <tbody>
          <tr>
            <td colSpan={6} className={cellBase}></td>
            <td className={hdrCell + " text-right"}>Subtotal</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.subtotal.toFixed(2)}</td>
          </tr>
          <tr>
            <td colSpan={6} className={cellBase}></td>
            <td className={hdrCell + " text-right"}>CGST @ {SAMPLE.cgst_rate}%</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.cgst.toFixed(2)}</td>
          </tr>
          <tr>
            <td colSpan={6} className={cellBase}></td>
            <td className={hdrCell + " text-right"}>SGST @ {SAMPLE.sgst_rate}%</td>
            <td className={cellBase + " text-right font-mono"}>{SAMPLE.sgst.toFixed(2)}</td>
          </tr>
          <tr style={{ background: "#FEF3C7" }}>
            <td colSpan={6} className={cellBase + " font-bold text-left"}>Amount in words: Rupees One Lakh Sixty-Eight Thousand One Hundred Fifty Only</td>
            <td className={cellBase + " font-bold text-right"}>GRAND TOTAL (₹)</td>
            <td className={cellBase + " font-bold text-right font-mono"}>{SAMPLE.total.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* Footer band */}
      <table className="w-full border-collapse -mt-px" style={{ tableLayout: "fixed" }}>
        <colgroup><col style={{ width: "50%" }} /><col style={{ width: "50%" }} /></colgroup>
        <tbody>
          <tr>
            <td className={hdrCell}>{form.invoice_show_bank_details !== false ? "Bank Details" : "Notes"}</td>
            <td className={hdrCell}>Terms & Conditions</td>
          </tr>
          <tr>
            <td className={cellBase + " whitespace-pre-line"} style={{ height: 55 }}>
              {form.invoice_show_bank_details !== false
                ? `${form.bank_name || "—"}\nA/c No: ${form.bank_account || "—"}\nIFSC: ${form.bank_ifsc || "—"} · Branch: ${form.bank_branch || "—"}`
                : (form.invoice_notes || "—")}
            </td>
            <td className={cellBase + " whitespace-pre-wrap"}>{form.terms || "—"}</td>
          </tr>
          <tr>
            <td colSpan={2} className={cellBase + " text-right font-bold"}>For {form.company_name || "Your Company"}</td>
          </tr>
          <tr>
            <td colSpan={2} className={cellBase + " text-right"} style={{ height: 30 }}>&nbsp;</td>
          </tr>
          <tr>
            <td colSpan={2} className={cellBase + " text-right"}>
              <div className="font-mono">{form.signatory_name || "________________________"}</div>
              <div className="italic text-gray-500">{form.signatory_designation || "Authorised Signatory"}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {form.invoice_footer && <div className="mt-2 text-center text-[9px] uppercase tracking-widest text-gray-400">{form.invoice_footer}</div>}
    </div>
  );
}

